import {
  HOTJAR_PROD_INLINE_SOURCE,
  HOTJAR_QA_INLINE_SOURCE,
  REDDIT_INLINE_SOURCE,
  VWO_INLINE_SOURCE,
} from "../trackingThirdPartySnippets";
import { getViteBuildEnv } from "./viteBuildEnv";

const appendHeadInlineScript = (source: string): void => {
  const el = document.createElement("script");
  el.type = "text/javascript";
  el.text = source;
  document.head.appendChild(el);
};

let hotjarInjected = false;
let vwoInjected = false;
let redditInjected = false;

const isThirdPartyInjectEnv = (): boolean => {
  const buildEnv = getViteBuildEnv();
  return buildEnv === "prod" || buildEnv === "qa" || buildEnv === "proto";
};

/**
 * After gtag consent updates: inject Hotjar / VWO / Reddit from OneTrust-mapped consent.
 * Hotjar → analytics; Reddit → ads; VWO → analytics or ads.
 *
 * Build env comes from `getViteBuildEnv()` (`vite.config.mts` `define`).
 * Without that, it is undefined in the browser and nothing here runs (GA can still load).
 *
 * `proto` is included for staging (`ENV=proto`); `scripts.ts` loads the gtag stub there too
 * so this path runs with `startOneTrustGtagSync`. Hotjar uses the QA snippet unless env is `"prod"`.
 *
 * See `util/oneTrust.ts` for CookiePro C0002 / C0004 → gtag.
 */
export const injectThirdPartyTagsForGtagConsent = (
  consent: Record<string, string>,
): void => {
  if (!isThirdPartyInjectEnv()) {
    return;
  }
  const analyticsGranted = consent.analytics_storage === "granted";
  const adsGranted = consent.ad_storage === "granted";

  if (!analyticsGranted) {
    hotjarInjected = false;
  }
  if (!adsGranted) {
    redditInjected = false;
  }
  if (!analyticsGranted && !adsGranted) {
    vwoInjected = false;
    return;
  }

  if (analyticsGranted && !hotjarInjected) {
    hotjarInjected = true;
    const hotjarSnippet =
      getViteBuildEnv() === "prod"
        ? HOTJAR_PROD_INLINE_SOURCE
        : HOTJAR_QA_INLINE_SOURCE;
    appendHeadInlineScript(hotjarSnippet);
  }
  if ((analyticsGranted || adsGranted) && !vwoInjected) {
    vwoInjected = true;
    appendHeadInlineScript(VWO_INLINE_SOURCE);
  }
  if (adsGranted && !redditInjected) {
    redditInjected = true;
    appendHeadInlineScript(REDDIT_INLINE_SOURCE);
  }
};

/** After full consent revoke: allow tags to inject again if consent is re-granted. */
export const resetDeferredThirdPartyInjectionState = (): void => {
  hotjarInjected = false;
  vwoInjected = false;
  redditInjected = false;
};
